import React, { MutableRefObject, SyntheticEvent, useState } from 'react';
import { SubmitHandler, useForm } from 'react-hook-form';
import styles from './FormSearchSav.module.css';
import { isPhoneNumber } from '../../utils/regexp';
import { useLocation, useNavigate } from 'react-router-dom';
import { APP_ROUTES } from '../../routes/AppRouter';
import axios, { AxiosInstance } from 'axios';
import { useAxiosInstance } from '../../contexts/AxiosInstanceProvider';
import { Sav } from '../../interfaces/Sav';

export default function FormSearchSav (): React.ReactElement {
  const { register, handleSubmit, getValues, setValue, formState, control } = useForm<FormInputs>({
    mode: 'onChange',
    defaultValues: {
      savNumber: '',
      phoneNumber: ''
    },
    delayError: 200
  });
  const { errors, isValid } = formState;
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [isValidating, setIsValidating] = useState<boolean>(false);
  const navigate = useNavigate();
  const [isForgottenNumber, setIsForgottenNumber] = useState<boolean>(false);
  const axiosInstance: MutableRefObject<AxiosInstance | undefined> = useAxiosInstance();
  const [isPhoneNumberError, setIsPhoneNumberError] = useState<boolean>(false);
  const location = useLocation();
  const [isSavNumberError, setIsSavNumberError] = useState<boolean>(location.state?.isNotFound);

  interface FormInputs {
    savNumber: string;
    phoneNumber: string;
  }

  async function getSavByMsisdn (msisdn: string) {
    setIsValidating(true);
    try {
      if (axiosInstance.current !== undefined) {
        const { data } = await axiosInstance.current.get<Sav>(
          '/api/sav_files',
          {
            params: {
              msisdn: msisdn
            }
          }
        );
        if (data !== undefined) {
          navigate(`${ APP_ROUTES.searchSav }/${ data.fileNumber }`);
        } else {
          setIsPhoneNumberError(true);
        }
        setIsValidating(false);
      }
    } catch (error) {
      if (axios.isAxiosError(error)) {
        console.error(error);
      } else {
        console.error('unexpected error: ', error);
      }
    }
  }

  const onSubmit: SubmitHandler<FormInputs> = (data: FormInputs) => {
    if (!control.getFieldState('savNumber').invalid && !isForgottenNumber) {
      navigate(`${ APP_ROUTES.searchSav }/${ data.savNumber }`);
    } else if (!control.getFieldState('phoneNumber').invalid) {
      let phoneNumber = data.phoneNumber.replace(/^\+?33|\|33/, '0');      
      phoneNumber = phoneNumber.replace(/\s/g, '');      
      getSavByMsisdn(phoneNumber);
    }
  };

  const handleChange = (event: SyntheticEvent) => {    
    setValue('savNumber', getValues('savNumber').trim(), {
      shouldValidate: true 
    });
    if (submitError !== null) {
      setSubmitError(null);
    }
    setIsPhoneNumberError(false);
    setIsSavNumberError(false);
  };

  const handleForgottenSavNumber = () => {
    setIsForgottenNumber(!isForgottenNumber);
  };

  return (
    <>
      <form onSubmit={ handleSubmit(onSubmit) }>
        { !isForgottenNumber &&
        <>
          <div className="row">
            <h2 className="title is-level-3">Je saisis mon numéro de dossier</h2>
          </div>
          <div className="row">
            <div className="columns is-aligned-center is-mobile">
              <div className="column">
                <div className="field">
                  <div className="control has-icons-right">
                    <label htmlFor="savNumber">Numéro de mon dossier</label>
                    <input
                      { ...register(
                        'savNumber', {
                          required: true,
                          minLength: {
                            value: 8,
                            message: 'Le numéro de dossier ne peut pas être inférieur à 8 caractères' 
                          },
                          maxLength: {
                            value: 8,
                            message: 'Le numéro de dossier ne peut pas être supérieur à 8 caractères'
                          },
                          onChange: handleChange
                        }, 
                      )
                      }
                      className={ `input ${ errors.savNumber || submitError ? 'is-error' : '' }` }
                      type="text"
                      placeholder="Votre numéro de dossier..."
                      aria-label="numéro de dossier"/>
                    { (errors.savNumber || submitError) &&
                    <span className={ `icon ${ styles.iconInInput }` }>
                      <i className="tri-exclamation-circle"></i>
                    </span>
                    }
                  </div>
                  { errors.savNumber?.message &&
                  <p className="help is-danger">{ errors.savNumber.message }</p>
                  }
                  { isSavNumberError && 
                  <div className="notification is-small has-body is-danger"
                    style={ {
                      margin: '10px 0px'
                    } }>
                    <div className="icon"
                      aria-label="Danger">
                      <i className="tri-exclamation-circle"
                        aria-hidden="true"></i>
                    </div>
                    <div className="body">
                      <p className="title">Le numéro de dossier saisi n&apos;existe pas</p>
                    </div>
                  </div>
                  }
                </div>
              </div>
            </div>
            <div className="columns">
              <div className="column is-12">
                { submitError !== null &&
                <div className="notification is-small has-body is-danger">
                  <span className="icon-and-text is-alternate">
                    <span className="icon is-small"
                      aria-label="erreur">
                      <i className="tri-exclamation-circle"
                        aria-hidden="true"></i>
                    </span>
                    <div className="body">
                      <span className="title">{ submitError }</span>
                    </div>
                  </span>
                </div>
                }
                <a className="link"
                  onClick={ handleForgottenSavNumber }>
                Numéro de dossier oublié ?
                </a>
              </div>
            </div>
          </div>
        </>
        }
        { isForgottenNumber &&
          <>
            <div className="row">
              <h2 className="title is-level-3">Je saisis mon numéro de téléphone</h2>
            </div>
            <div className="row">
              <div className="columns is-aligned-center is-mobile">
                <div className="column">
                  <div className="field">
                    <div className="control has-icons-right">
                      <label htmlFor="phoneNumber">Votre numéro de téléphone mobile</label>
                      <input
                        { ...register(
                          'phoneNumber', {
                            required: true,
                            pattern: {
                              value: isPhoneNumber,
                              message: 'Numéro de téléphone non valide. Exemple : 0601020304, +33 6 01 02 03 04'
                            },
                            onChange: handleChange
                          }
                        )
                        }
                        className={ `input ${ errors.phoneNumber || submitError ? 'is-error' : '' }` }
                        type="text"
                        placeholder="Votre numéro de téléphone mobile..."
                        aria-label="numéro de téléphone"/>
                      { (errors.phoneNumber || submitError) &&
                        <span className={ `icon ${ styles.iconInInput }` }>
                          <i className="tri-exclamation-circle"></i>
                        </span>
                      }
                    </div>
                    { errors.phoneNumber?.message &&
                      <p className="help is-danger">{ errors.phoneNumber.message }</p>
                    }
                    { isPhoneNumberError && 
                    <div className="notification is-small has-body is-danger"
                      style={ {
                        margin: '10px 0px'
                      } }>
                      <div className="icon"
                        aria-label="Danger">
                        <i className="tri-exclamation-circle"
                          aria-hidden="true"></i>
                      </div>
                      <div className="body">
                        <p className="title">Aucun dossier n&apos;est lié à ce numéro de téléphone</p>
                      </div>
                    </div>
                    }
                  </div>
                </div>
              </div>
              <div className="columns">
                <div className="column is-12">
                  { submitError !== null &&
                    <div className="notification is-small has-body is-danger">
                      <span className="icon-and-text is-alternate">
                        <span className="icon is-small"
                          aria-label="erreur">
                          <i className="tri-exclamation-circle"
                            aria-hidden="true"></i>
                        </span>
                        <div className="body">
                          <span className="title">{ submitError }</span>
                        </div>
                      </span>
                    </div>
                  }
                  <a className={ `text link has-icon ${ styles.iconLinkLeft }` }
                    onClick={ handleForgottenSavNumber }>
                    <span className="icon is-small">
                      <i className="tri-arrow-left"
                        aria-hidden="true"></i>
                    </span>
                    <span className="text">J&apos;ai retouvé mon numéro de dossier</span>
                  </a>
                </div>
              </div>
            </div>
          </>
        }

        <div className="row">
          <div className="columns">
            <div className="column is-12">
              <button className={ `button is-primary ${ isValidating ? 'is-loading' : '' }` }
                type="submit"
                aria-label="submit input"
                disabled={ !isValid }>
                Je valide
              </button>
            </div>
          </div>
        </div>
      </form>
    </>
  );
}